import React, { useCallback, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import SectionTitle from "../../../components/section/SectionTitle";
import SectionHeader from "../../../components/section/SectionHeader";
import PaginatedView from "../../../components/pagination/PaginatedView";
import { getInvoices } from "../../../services/billing-invoice";
import BillingInvoiceTable from "../../../components/billing-invoice/BillingInvoiceTable";
import { paginationOffset } from "../../../helpers/pagination";

import SearchView from "../../../components/search/SearchView";
import { formatAppDate } from "../../../helpers/date-formatter";

const path = "invoice";

function BillingInvoice() {
  const [invoices, setInvoices] = useState([]);
  const [invoiceResponse, setInvoiceResponse] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { page: pageNum } = params;

  useEffect(() => {
    const user = searchParams.get("user");
    const startDate = searchParams.get("start");
    const endDate = searchParams.get("end");
    const sortOrder = searchParams.get("sort");
    const sortBy = searchParams.get("sortBy");

    if (startDate !== null) {
      setStartDate(startDate);
    }

    if (endDate !== null) {
      setEndDate(endDate);
    }

    if (user !== null) {
      setSearchText(user);
    }

    setSortBy(sortBy);

    fetchInvoices(user, startDate, endDate, sortOrder, sortBy);
  }, [params, searchParams]);

  const fetchInvoices = async (
    user = null,
    startDate = null,
    endDate = null,
    sortOrder = null,
    sortBy = null
  ) => {
    try {
      const response = await getInvoices(
        pageNum,
        user,
        startDate,
        endDate,
        sortOrder,
        sortBy
      );
      setInvoiceResponse(response);
      setInvoices(response.data);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSearch = (text, start, end) => {
    const params = [];

    if (text.length > 0) {
      params.push(`user=${encodeURIComponent(text)}`);
    }

    if (start !== null) {
      params.push(`start=${formatAppDate(start)}`);
    }

    if (end !== null) {
      params.push(`end=${formatAppDate(end)}`);
    }

    if (params.length > 0) {
      navigate(`${location.pathname}?${params.join("&")}`);
    }
  };

  const handleSorting = (sortKey) => {
    let params = [];
    let sortOrder = "";
    let currentSortBy = "";

    searchParams.forEach((value, key) => {
      if (key === "sort") {
        sortOrder = value;
      } else if (key === "sortBy") {
        currentSortBy = value;
      } else {
        params.push(key + "=" + value);
      }
    });

    params.push("sortBy=" + sortKey);

    if (currentSortBy === sortKey) {
      if (sortOrder === "" || sortOrder === "DESC") {
        sortOrder = "ASC";
      } else if (sortOrder === "ASC") {
        sortOrder = "DESC";
      }
      params.push("sort=" + sortOrder);
    }

    const queryParams = params.join("&");
    navigate(`/${path}?${queryParams}`, { replace: true });
  };

  const buildPaginationLink = useCallback(() => {
    const user = searchParams.get("user");
    const start = searchParams.get("start");
    const end = searchParams.get("end");

    const queryParams = [];
    if (user !== null) {
      queryParams.push(`user=${user}`);
    }
    if (start !== null) {
      queryParams.push(`start=${start}`);
    }
    if (end !== null) {
      queryParams.push(`end=${end}`);
    }

    const queryString =
      queryParams.length > 0 ? `${queryParams.join("&")}` : "";

    return queryString;
  }, [searchParams]);

  return (
    <>
      <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white p-2">
        <div className="rounded-t mb-0 px-4 py-3 pb-8 border-0">
          <div className="flex flex-col lg:flex-row justify-end gap-2">
            <div className="flex w-full">
              <SectionHeader>
                <SectionTitle title={"Invoices"} />
              </SectionHeader>

              <SearchView
                onSearch={handleSearch}
                search={searchText}
                start={startDate}
                end={endDate}
              />
            </div>
          </div>
        </div>

        <BillingInvoiceTable
          data={invoices}
          offset={paginationOffset(invoiceResponse?.metadata)}
          path={path}
          sort={sortBy}
          onSort={handleSorting}
        />

        {invoiceResponse?.metadata?.totalPages > 1 ? (
          <div className="py-2 flex justify-end my-6">
            <PaginatedView
              path={`/${path}/page`}
              query={buildPaginationLink()}
              total={invoiceResponse?.metadata.totalPages}
              current={invoiceResponse?.metadata?.currentPage}
              next={invoiceResponse?.metadata?.nextPage}
              previous={invoiceResponse?.metadata?.previousPage}
            />
          </div>
        ) : null}
      </div>
    </>
  );
}

export default BillingInvoice;
